import { Injectable } from '@angular/core';
import { HttpQueryBuilder } from '@portal-shared/app/builders/http-query.builder';
import { SortDirectionEnum } from '@portal-shared/app/enums/sort-directions.enum';
import { AllyaStorageService } from '@services/allya-storage.service';
import { RequestListFilter as RequestBoardFilter } from '@services/models/request-list-filterby.model';
import { BehaviorSubject } from 'rxjs';
import { FilterUpdate } from './models/filter-update.model';

@Injectable({
  providedIn: 'root'
})
export class RequestBoardFiltersService {
  public isFiltersOpen = false;

  private filterName = 'RequestBoardFilters';

  private filters$ = new BehaviorSubject<FilterUpdate>({
    key: undefined,
    filters: this.getDefaultFilters()
  });

  public constructor(private allyaStorageService: AllyaStorageService) {}

  public init() {
    const filters = this.allyaStorageService.getItem<RequestBoardFilter>(this.filterName);
    if (filters) {
      this.filters$.next({
        filters
      });
    } else {
      this.clearFilters();
    }
  }

  private getDefaultFilters(): RequestBoardFilter {
    return {
      sortDirection: SortDirectionEnum.Asc,
      orderBy: 'label',
      classes: [],
      departments: []
    };
  }

  public getFilters() {
    return this.filters$.getValue().filters;
  }

  public getHiddenActiveFiltersCount() {
    const filter = this.getFilters();
    const defaultFilter = this.getDefaultFilters();

    let count = 0;
    if (filter.classes.length) {
      count++;
    }
    if (filter.departments.length) {
      count++;
    }
    if (filter.orderBy !== defaultFilter.orderBy || filter.sortDirection !== defaultFilter.sortDirection) {
      count++;
    }

    return `${count}`;
  }

  public clearFilters(options?: { keepSearch?: boolean }) {
    const filters = this.getFilters();

    const newFilter = this.getDefaultFilters();

    if (options?.keepSearch) {
      newFilter.searchKeys = filters.searchKeys;
    }

    this.filters$.next({
      filters: newFilter
    });
  }

  public updateFilter<TKey extends keyof RequestBoardFilter>(key: TKey, value: RequestBoardFilter[TKey]) {
    const filters = this.getFilters();
    filters[key] = value;
    this.filters$.next({
      key,
      filters
    });
    this.allyaStorageService.setItem(this.filterName, filters);
    return filters;
  }

  public observeChanges() {
    return this.filters$.asObservable();
  }

  public apply(query: HttpQueryBuilder) {
    const filters = this.getFilters();

    if (filters.searchKeys) {
      query.search(filters.searchKeys.join(','));
    }

    if (filters.departments.length) {
      query.filterBy(
        'departmentIds',
        filters.departments.map(c => c.id)
      );
    }

    if (filters.classes.length) {
      query.filterBy(
        'classIds',
        filters.classes.map(c => c.id)
      );
    }

    const orderBy = filters.orderBy ?? 'label';
    query.sort([{ colId: orderBy, sort: filters.sortDirection }]);

    return query;
  }
}
